/** CSS contract: css-library/components/02-containers/signature-pad.css. */
import type { Ref } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { HTMLAttributes, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { Prompt, PromptActions } from './Prompt';
import { ModalHead } from './ModalHead';
import { Button } from './Button';
import { Title } from './Title';

function croppedSignature(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): string | null {
  const { width: w, height: h } = canvas;
  const data = ctx.getImageData(0, 0, w, h).data;
  let minX = w;
  let minY = h;
  let maxX = 0;
  let maxY = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX <= minX) return null;
  const pad = 6;
  const cw = maxX - minX + 2 * pad;
  const ch = maxY - minY + 2 * pad;
  const cropped = document.createElement('canvas');
  cropped.width = cw;
  cropped.height = ch;
  const croppedCtx = cropped.getContext('2d');
  if (!croppedCtx) return null;
  croppedCtx.drawImage(canvas, minX - pad, minY - pad, cw, ch, 0, 0, cw, ch);
  return cropped.toDataURL('image/png');
}

export interface SignaturePromptProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children' | 'onCancel'> {
  title: ReactNode;
  children?: ReactNode;
  clearLabel: ReactNode;
  cancelLabel: string;
  confirmLabel: ReactNode;
  onCancel: () => void;
  busy?: boolean;
  secondaryAction?: ReactNode;
  onCapture: (dataUrl: string) => void;
  cardClassName?: string;
}

export function SignaturePrompt(
  { ref, title,
    children,
    clearLabel,
    cancelLabel,
    confirmLabel,
    secondaryAction,
    busy = false,
    onCancel,
    onCapture,
    className,
    cardClassName,
    ...rest
  }: SignaturePromptProps & { ref?: Ref<HTMLDivElement> },
) {
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawingRef = useRef(false);
  const sizedRef = useRef(false);
  function sizeCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    ctxRef.current = ctx;
    sizedRef.current = true;
  }

  function clearPad() {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }

  useEffect(() => {
    sizedRef.current = false;
    const raf = requestAnimationFrame(() => {
      sizeCanvas();
      clearPad();
    });
    return () => cancelAnimationFrame(raf);
  }, []);
  function pos(e: ReactPointerEvent<HTMLCanvasElement>): [number, number] {
    const r = e.currentTarget.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }

  function onPointerDown(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (!sizedRef.current) sizeCanvas();
    const ctx = ctxRef.current;
    if (!ctx) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    ctx.beginPath();
    const [x, y] = pos(e);
    ctx.moveTo(x, y);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const ctx = ctxRef.current;
    if (!ctx) return;
    const [x, y] = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasDrawn) setHasDrawn(true);
  }

  function onPointerUp() {
    drawingRef.current = false;
  }

  function handleConfirm() {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    const dataUrl = croppedSignature(canvas, ctx);
    if (!dataUrl) return;
    onCapture(dataUrl);
  }

  return (
    <Prompt
      ref={ref}
      className={className}
      cardClassName={cardClassName}
      busy={busy}
      onEscape={onCancel}
      aria-label={typeof title === 'string' ? title : undefined}
      topbar={<ModalHead closeLabel={cancelLabel} closeDisabled={busy} onClose={onCancel} />}
      {...rest}
    >
      <div className="sign-body">
        <Title as="h3">{title}</Title>
        {children != null && <p className="sign-hint">{children}</p>}
        <div className="sign-pad">
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
          <span className="sign-x" aria-hidden="true">
            ✕
          </span>
        </div>
      </div>
      <PromptActions>
        <Button disabled={busy} onClick={clearPad}>{clearLabel}</Button>
        <Button variant="primary" loading={busy} disabled={!hasDrawn} onClick={handleConfirm}>
          {confirmLabel}
        </Button>
      </PromptActions>
      {secondaryAction != null && <PromptActions>{secondaryAction}</PromptActions>}
    </Prompt>
  );
}
